import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { MyCommunityMoovForm } from './MyCommunityMoovForm/MyCommunityMoovForm'
import {
  TBusinessAccountDetails,
  TBusinessAccountDocument,
  TBusinessAccountUnderwriting
} from './types'
import {
  getBusinessAccountDocumentsRequest,
  getBusinessAccountUnderwritingRequest
} from './api'
import { updateLoadingStack } from 'redux/store/common/slice'
import { FETCH_STATUS } from 'redux/store/common/types'
import { Banner } from 'components/Banner/Banner'
import { RowLayout } from 'features/MyCommunity/Vendors/VendorCreation/General/VendorForm/RowLayout'
import { Separator } from 'components/Separator/Separator'
import { Status } from 'components/Status'
import {
  MOOV_BUSINESS_ACCOUNT_STATUS_TO_CLASSNAME,
  MOOV_BUSINESS_ACCOUNT_STATUS_TO_LABEL,
  ONBOARDING_SESSION_STATUS
} from './constants'
import cn from 'classnames'
import { ResolutionLinkWarningBlockContainer } from './ResolutionLink/ResolutionLinkWarningBlockContainer'
import { DocumentFailerRiskWarningBanner } from './DocumentFailerRiskWarningBanner'
import { BUTTON_TYPES } from 'components/Button'
import { BusinessDetailsCommunityEmptyState } from 'features/PaymentDetails/BusinessDetails/CommunityEmptyState'
import { BusinessAccountDetails } from './BusinessAccountDetails'
import './styles.scss'
import { TCommunityEntity } from './MyCommunityMoovForm/types'

export const BankingSetupContainer = ({
  businessAccountDetails,
  moovBusinessAccountId,
  refreshDetails,
  communityEntity
}: {
  businessAccountDetails?: TBusinessAccountDetails
  moovBusinessAccountId?: string | null
  refreshDetails: () => void
  communityEntity: TCommunityEntity
}) => {
  const dispatch = useDispatch()
  const [predefinedAccountId, setPredefinedAccountId] = useState<
    string | undefined
  >(undefined)
  const [businessAccountUnderwriting, setBusinessAccountUnderwriting] =
    useState<TBusinessAccountUnderwriting | undefined>(undefined)
  const [businessAccountDocuments, setBusinessAccountDocuments] = useState<
    TBusinessAccountDocument[]
  >([])

  const [openMoovForm, setOpenMoovForm] = useState(false)

  const getBusinessAccountUnderwriting = useCallback(async () => {
    if (!moovBusinessAccountId) return
    try {
      dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_STARTED))
      const res = await getBusinessAccountUnderwritingRequest(
        moovBusinessAccountId
      )
      setBusinessAccountUnderwriting(res.data)
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_FINISHED))
    }
  }, [moovBusinessAccountId, dispatch])

  const getBusinessAccountDocuments = useCallback(async () => {
    if (!moovBusinessAccountId) return
    try {
      dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_STARTED))
      const res = await getBusinessAccountDocumentsRequest(
        moovBusinessAccountId
      )
      setBusinessAccountDocuments(res.data.results)
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(updateLoadingStack(FETCH_STATUS.REQUEST_FINISHED))
    }
  }, [moovBusinessAccountId, dispatch])

  useEffect(() => {
    if (moovBusinessAccountId) {
      getBusinessAccountUnderwriting()
      getBusinessAccountDocuments()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moovBusinessAccountId])

  useEffect(() => {
    if (!businessAccountDetails) return
    if (
      businessAccountDetails.onboarding_session.status &&
      businessAccountDetails.onboarding_session.status !==
        ONBOARDING_SESSION_STATUS.COMPLETED
    ) {
      setPredefinedAccountId(businessAccountDetails.moov_account_id)
    }
  }, [businessAccountDetails])

  const showCompleteSetupBanner = useMemo(() => {
    return (
      businessAccountDetails?.moov_account_id &&
      businessAccountDetails.onboarding_session.status !==
        ONBOARDING_SESSION_STATUS.COMPLETED
    )
  }, [businessAccountDetails])

  const handleRefreshDetails = useCallback(() => {
    refreshDetails()
    getBusinessAccountUnderwriting()
    getBusinessAccountDocuments()
  }, [
    refreshDetails,
    getBusinessAccountUnderwriting,
    getBusinessAccountDocuments
  ])

  const onCancelMoovForm = useCallback(() => {
    setOpenMoovForm(false)
    handleRefreshDetails()
  }, [handleRefreshDetails])

  const onAddAccount = useCallback(() => {
    if (
      businessAccountDetails?.onboarding_session.status !==
      ONBOARDING_SESSION_STATUS.COMPLETED
    ) {
      setOpenMoovForm(true)
    }
  }, [businessAccountDetails?.onboarding_session.status])

  return (
    <div className="column gap-16">
      {businessAccountDetails?.moov_account_id ? (
        <>
          <ResolutionLinkWarningBlockContainer
            businessAccountDetails={businessAccountDetails}
            successCallback={handleRefreshDetails}
          />
          <DocumentFailerRiskWarningBanner
            businessAccountDetails={businessAccountDetails}
          />
          {showCompleteSetupBanner && (
            <Banner.Warning
              text="Finish your banking setup to start receiving payments"
              subtext="Complete all required steps to finish setting up your account."
              button={[
                {
                  buttonType: BUTTON_TYPES.PRIMARY,
                  buttonText: 'Continue setup',
                  onButtonClick: onAddAccount
                }
              ]}
            />
          )}
          {businessAccountDetails?.verification_status && (
            <>
              <RowLayout title="Status">
                <Status.Default
                  className={cn(
                    MOOV_BUSINESS_ACCOUNT_STATUS_TO_CLASSNAME[
                      businessAccountDetails?.verification_status
                    ],
                    'mt-8'
                  )}
                >
                  {
                    MOOV_BUSINESS_ACCOUNT_STATUS_TO_LABEL[
                      businessAccountDetails?.verification_status
                    ]
                  }
                </Status.Default>
              </RowLayout>
              <Separator />
            </>
          )}
          <BusinessAccountDetails
            businessAccountUnderwriting={businessAccountUnderwriting}
            businessAccountDetails={businessAccountDetails}
            businessAccountDocuments={businessAccountDocuments}
          />
        </>
      ) : (
        <BusinessDetailsCommunityEmptyState onClick={onAddAccount} />
      )}
      <MyCommunityMoovForm
        communityEntity={communityEntity}
        openForm={openMoovForm}
        onCancel={onCancelMoovForm}
        onSuccessCallback={handleRefreshDetails}
        predefinedAccountId={predefinedAccountId}
        predefinedOnboadingSessionUuid={
          businessAccountDetails?.onboarding_session.uuid
        }
      />
    </div>
  )
}
